import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PageWrapper from "../pageWrapper";
import { GoogleLogin } from "@react-oauth/google";
import { GoogleLoginUser, Login } from "../../BackendAccess/UserHooks";

// CSS
import "../0PageStyles.css";
import "./loginPage.css";

// Components
import InputField from "../../Components/Other Components/inputField";
import Button from "../../Components/Other Components/button";
import Title from "../../Components/Other Components/Title";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

// Contexts & Hooks
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useLogin } from "../../Contexts/LoginContext";

const GOOGLE_LOCALE_MAP = { rs: "sr" };

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(false);
  const navigate = useNavigate();

  const { showNotification } = useNotification();
  const { loggedIn, refreshLoginStatus, setLoggedIn } = useLogin();

  const handleLogin = async () => {
    setLoading(true);
    try {
      const [data, err] = await Login(usernameOrEmail, password);
      if (err) {
        if (err.errCode === 403 && err.params?.email) {
          navigate("/notVerifiedPage", {
            state: { PageType: "Verification", email: err.params.email },
          });
        }
        setError(err);
      } else {
        localStorage.setItem("token", data.token);
        showNotification(t("notifications.loginSuccess"), "success");
        refreshLoginStatus();
      }
    } catch {
      setError({ err: t("errors.serverError"), errCode: 500 });
    } finally {
      setLoading(false);
    }
  };

  const handleLoginWithGoogle = async (credentialResponse) => {
    setLoading(true);
    try {
      const [data, err] = await GoogleLoginUser(credentialResponse.credential);
      if (err) {
        setError(err);
      } else {
        localStorage.setItem("token", data.token);
        showNotification(t("notifications.googleLoginSuccess"), "success");
        refreshLoginStatus();
      }
    } catch {
      setError({ err: t("notifications.googleSignInFailed"), errCode: 500 });
    } finally {
      setLoading(false);
    }
  };

  useErrorHandler(error, showNotification, setPageError);

  return (
    <PageWrapper>
      <PageLoader loading={loading} error={pageError}>
        <>
          <div className="page-content">
            <div className="spacer l" />
            <div className="container-bordered vertical login w-auto">
              <Title margin={false} textPosition="center">
                {t("login.title")}
              </Title>
              <InputField
                placeholder={t("login.usernameOrEmailPlaceholder")}
                label={t("login.usernameLabel")}
                value={usernameOrEmail}
                inputMargins={["0px", "0.7rem", "0.7rem", "0.7rem"]}
                onChange={setUsernameOrEmail}
                labelPosition="top-left"
                mandatory
              />
              <InputField
                placeholder={t("login.passwordPlaceholder")}
                label={t("login.passwordLabel")}
                type="password"
                value={password}
                inputMargins={["0rem", "0.7rem", "0.7rem", "0.7rem"]}
                onChange={setPassword}
                labelPosition="top-left"
                mandatory
              />
              <MandatoryExplanation />
              <div className="btn-container">
                <Button
                  className="mt-3 mb-3"
                  btnStyle="btn-positive"
                  onClick={handleLogin}
                  acceptEnter
                >
                  {t("login.loginButton")}
                </Button>
              </div>
              <GoogleLogin
                className="my-custom-google-btn"
                onSuccess={handleLoginWithGoogle}
                onError={() => {
                  showNotification("Google Sign-In failed", "error");
                }}
                text="continue_with"
                ux_mode="popup"
                locale={GOOGLE_LOCALE_MAP[i18n.language] || i18n.language}
              />
              <p className="text-prompt">
                {t("login.noAccount")}{" "}
                <span
                  className="text-link"
                  onClick={() => navigate("/register")}
                >
                  {t("login.registerHere")}
                </span>
              </p>
              <p className="text-prompt mt-0">
                {t("login.forgotPassword")}{" "}
                <span
                  className="text-link"
                  onClick={() => navigate("/passwordResetSentPage")}
                >
                  {t("login.clickHere")}
                </span>
              </p>
            </div>
          </div>
        </>
      </PageLoader>
    </PageWrapper>
  );
}
